import dayjs, { type Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { getErrorMessage } from "./utils";
import logger from "./logger";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(customParseFormat);
dayjs.extend(timezone);
dayjs.extend(utc);

export type DateRange = {
  start: Dayjs;
  end: Dayjs;
};

export type BidWindowInfo = {
  startBidWindow: Dayjs;
  endBidWindow: Dayjs;
  isPreBidWindow: boolean;
  isPostBidWindow: boolean;
  isBidWindowOpen: boolean;
};

// todo: migrate to composable, so that we can get the offsetHours from apiInfo

/**
 * Calculate start and end of this date period in dayjs, America/Edmonton.
 *
 * @param schedulePeriodId eg. 2024_Nov_Dec
 * @returns eg.  Nov 01, 2024 00:00:00 GMT-0600 to Dec 31, 2024 23:59:59 GMT-0700
 */
export const schedPeriodToDateRange = (schedulePeriodId: string) => {
  if (schedulePeriodId === "") {
    throw new Error("Empty schedule period");
  }
  const year = schedulePeriodId.substring(0, 4);
  const startMonth = schedulePeriodId.substring(5, 8);

  const start = dayjs.tz(
    `${year}-${startMonth}-01`,
    "YYYY-MMM-DD",
    "America/Edmonton",
  );
  const end = start.add(1, "months").endOf("month");
  return { start, end } as DateRange;
};

/**
 * Get the previous schedule period based on the given schedule period id.
 *
 * @param schedPeriodId the schedule period id to get the previous schedule period for
 * @returns the previous schedule period id
 */
export const getPreviousSchedPeriod = (schedPeriodId: string) => {
  const dateRange = schedPeriodToDateRange(schedPeriodId);
  const startDate = dateRange.start.subtract(2, "months");
  const endDate = startDate.add(1, "month").endOf("month");
  return `${startDate.format("YYYY_MMM")}_${endDate.format("MMM")}`;
};

/**
 * Get the next schedule period based on the given schedule period id.
 *
 * @param schedPeriodId the schedule period id to get the previous schedule period for
 * @returns the previous schedule period id
 */
export const getNextSchedPeriod = (schedPeriodId: string) => {
  const dateRange = schedPeriodToDateRange(schedPeriodId);
  const startDate = dateRange.start.add(2, "months");
  const endDate = startDate.add(1, "month").endOf("month");
  return `${startDate.format("YYYY_MMM")}_${endDate.format("MMM")}`;
};

/**
 * Get the start and end (plus some convenience props) for the bidWindow for the provided schedPeriod.
 */
export const bidWindowInfo = (
  schedPeriodId: string,
  offsetHours: number = 0,
) => {
  // just some defaults
  if (!schedPeriodId) {
    return {
      startBidWindow: dayjs().subtract(1, "days").startOf("day"),
      endBidWindow: dayjs().subtract(1, "days").endOf("day"),
      isBidWindowOpen: false,
      isPreBidWindow: false,
      isPostBidWindow: false,
    };
  }

  const now = getNow();
  const { start } = schedPeriodToDateRange(schedPeriodId);
  const startBidWindow = start.subtract(2, "months").add(offsetHours, "hours");
  const endBidWindow = start
    .subtract(2, "months")
    .set("date", 9)
    .set("hour", 17)
    .set("minute", 0);

  const isBidWindowOpen =
    !now.isBefore(startBidWindow) && !now.isAfter(endBidWindow);

  return {
    isPreBidWindow: now.isBefore(startBidWindow),
    isPostBidWindow: now.isAfter(endBidWindow),
    startBidWindow,
    endBidWindow,
    isBidWindowOpen,
  };
};

/**
 * This is a schedule that is no longer active. This means that the current date has passed the end of
 * this schedPeriod.
 */
export const hasSchedulePassed = (schedPeriodId: string) => {
  try {
    const dateRange = schedPeriodToDateRange(schedPeriodId);
    return getNow().isAfter(dateRange.end);
  } catch (e) {
    logger.warn("Returning true for passed schedule:", getErrorMessage(e));
    return true;
  }
};

/**
 * This is a test aware version of now that can be set to a specific date for testing
 */
export const getNow = () => {
  let now = dayjs().tz();
  const appDate = dayjs(sessionStorage.getItem("appDate"));
  if (appDate.isValid()) {
    now = appDate;
  }
  return now;
};

/**
 * Get the schedPeriod for any particular date.
 *
 * @param date
 * @returns the schedPeriodId
 */
export const schedPeriodForDate = (date: Dayjs): string => {
  const month = date.get("month");
  const schedStartMonth = month - (month % 2);
  const startDate = date.set("month", schedStartMonth).startOf("month");
  const endDate = startDate.add(1, "month").endOf("month");
  return startDate.format("YYYY_MMM") + endDate.format("_MMM");
};

/**
 * Get the date from a dayId
 * @param dayId the dayId (2023-01-01 is the magic day 1)
 * @returns the date
 */
export function getDateFromDayId(dayId: number): Dayjs {
  const startOfTime: Dayjs = dayjs.tz("2023-01-01", "America/Edmonton").startOf("day");
  return startOfTime.add(dayId, "day");
}

/**
 * Get the dayId from a date
 */
export function getDayIdFromDate(date: Dayjs): number {
  const startOfTime: Dayjs = dayjs.tz("2023-01-01", "America/Edmonton").startOf("day");
  return date.startOf("day").diff(startOfTime, "days");
}

export function getVacationValidStartDate(
  date: Dayjs,
  offsetHours: number = 0,
) {
  const currPeriod = schedPeriodForDate(date);

  function findNextValidVacationUploadStart(period: string) {
    const nextBidWindow = bidWindowInfo(period, offsetHours);
    if (date.isAfter(nextBidWindow.startBidWindow, "day")) {
      // If the date is after the current bid window, we need to get the next sched period
      const nextPeriod = getNextSchedPeriod(period);
      return findNextValidVacationUploadStart(nextPeriod);
    }
    return schedPeriodToDateRange(period).start;
  }

  return findNextValidVacationUploadStart(currPeriod).format("YYYY-MM-DD");
}

/**
 * This is the next sched period from the current date. So if you were bidding,
 * what would that schedPeriod be that you are bidding on?
 *
 * eg. if it's July 10, then schedPeriod would be Sept_Oct.
 * if it's Sept 2, then it's also Sept_Oct
 */
export function getBiddingSchedPeriod(): string {
  const now = getNow();
  const schedPeriod = schedPeriodForDate(now);
  const bidWindow = bidWindowInfo(schedPeriod);
  if (bidWindow.isBidWindowOpen) {
    return schedPeriod;
  }
  return getNextSchedPeriod(schedPeriod);
}

/**
 * Does this day fall within the previous 5 days before the start of the schedPeriod?
 * 
 * @param dayId 
 * @param schedPeriodId 
 * @returns true if within the prev 5 days
 */
export function isPrev5Days(dayId: number, schedPeriodId: string): boolean {
  const dateRange = schedPeriodToDateRange(schedPeriodId);
  const date = getDateFromDayId(dayId);
  return date.isBefore(dateRange.start, "day") && date.isSameOrAfter(dateRange.start.subtract(5, "days"), "day");
}